import React, { useState, useRef } from "react";
import MarkdownIt from "markdown-it";
import mk from "markdown-it-katex";
import texmath from "markdown-it-texmath";
import "katex/dist/katex.min.css";
import htmlDocx from "html-docx-js/dist/html-docx";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const mdParser = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
})
  .use(mk)
  .use(texmath, {
    engine: require("katex"),
    delimiters: "dollars",
    katexOptions: {
      macros: {
        "\\ce": "\\mathrm", // simple macro for chemical equations
      },
    },
  });

export default function MarkdownRendererApp() {
  const [markdownText, setMarkdownText] = useState("");
  const [renderedHTML, setRenderedHTML] = useState("");
  const renderRef = useRef(null);

  // Handle markdown input change
  const handleInputChange = (e) => {
    setMarkdownText(e.target.value);
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith(".md")) {
      alert("Please upload a Markdown (.md) file.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setMarkdownText(event.target.result);
    };
    reader.readAsText(file);
  };

  // Render markdown to HTML
  const handleRender = () => {
    try {
      const html = mdParser.render(markdownText);
      setRenderedHTML(html);
    } catch (err) {
      alert("Error rendering markdown: " + err.message);
    }
  };

  // Copy rendered HTML to clipboard (rich text)
  const handleCopy = async () => {
    if (!renderedHTML) {
      alert("Nothing to copy. Please render first.");
      return;
    }
    try {
      // Create a temporary element to hold the HTML
      const container = document.createElement("div");
      container.innerHTML = renderedHTML;
      document.body.appendChild(container);

      // Use Clipboard API with HTML format
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([renderedHTML], { type: "text/html" }),
          "text/plain": new Blob([container.innerText], { type: "text/plain" }),
        }),
      ]);

      document.body.removeChild(container);
      alert("Rendered content copied to clipboard!");
    } catch (err) {
      alert("Failed to copy: " + err.message);
    }
  };

  // Download as PDF
  const handleDownloadPDF = async () => {
    if (!renderRef.current) {
      alert("Nothing to download. Please render first.");
      return;
    }
    try {
      const element = renderRef.current;

      // Use html2canvas to capture rendered content as canvas
      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF({
        unit: "pt",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Calculate image dimensions to fit page width
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pageWidth;
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let position = 0;

      if (pdfHeight < pageHeight) {
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
      } else {
        // If content is taller than a page, split it (basic approach)
        let heightLeft = pdfHeight;
        while (heightLeft > 0) {
          pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
          heightLeft -= pageHeight;
          position -= pageHeight;
          if (heightLeft > 0) pdf.addPage();
        }
      }

      pdf.save("rendered_markdown.pdf");
    } catch (err) {
      alert("Failed to generate PDF: " + err.message);
    }
  };

  // Download as Word (.docx)
  const handleDownloadDocx = () => {
    if (!renderedHTML) {
      alert("Nothing to download. Please render first.");
      return;
    }
    try {
      // Wrap HTML in a full document structure
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Document</title><link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.css"></head><body>${renderedHTML}</body></html>`;

      const converted = htmlDocx.asBlob(html, { orientation: "portrait" });

      const url = URL.createObjectURL(converted);
      const a = document.createElement("a");
      a.href = url;
      a.download = "rendered_markdown.docx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to generate Word document: " + err.message);
    }
  };

  // Download as OpenDocument Text (.odt)
  // Note: Generating ODT is complex; here we do a basic approach by converting HTML to ODT using a minimal template.
  // For production, consider using a dedicated library or server-side conversion.
  const handleDownloadOdt = () => {
    if (!renderedHTML) {
      alert("Nothing to download. Please render first.");
      return;
    }
    try {
      // Minimal ODT content with embedded HTML (this is a simplified hack)
      // Real ODT is zipped XML files, so here we fallback to saving HTML with .odt extension
      const blob = new Blob([renderedHTML], { type: "application/vnd.oasis.opendocument.text" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "rendered_markdown.odt";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to generate OpenDocument file: " + err.message);
    }
  };

  return (
    <div style={{ maxWidth: 900, margin: "auto", fontFamily: "Arial, sans-serif" }}>
      <h1>Hani's Markdown Renderer with Math & Chemistry</h1>

      <div style={{ marginBottom: 12 }}>
        <label>
          Paste your Markdown here:
          <textarea
            value={markdownText}
            onChange={handleInputChange}
            rows={10}
            style={{ width: "100%", fontFamily: "monospace", fontSize: 14, marginTop: 4 }}
            placeholder="Enter Markdown with math formulas ($...$) and chemical equations ($\\ce{H2O}$)..."
          />
        </label>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label>
          Or upload a Markdown file:
          <input type="file" accept=".md" onChange={handleFileUpload} style={{ marginLeft: 8 }} />
        </label>
      </div>

      <button onClick={handleRender} style={{ padding: "8px 16px", marginBottom: 20 }}>
        Render Markdown
      </button>

      {renderedHTML && (
        <>
          <h2>Rendered Output</h2>
          <div
            ref={renderRef}
            style={{
              border: "1px solid #ccc",
              padding: 20,
              borderRadius: 4,
              minHeight: 200,
              overflowY: "auto",
              backgroundColor: "#fefefe",
            }}
            dangerouslySetInnerHTML={{ __html: renderedHTML }}
          />

          <div style={{ marginTop: 20, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={handleCopy} style={{ padding: "8px 16px" }}>
              Copy Rendered Output
            </button>
            <button onClick={handleDownloadPDF} style={{ padding: "8px 16px" }}>
              Download as PDF
            </button>
            <button onClick={handleDownloadDocx} style={{ padding: "8px 16px" }}>
              Download as Word (.docx)
            </button>
            <button onClick={handleDownloadOdt} style={{ padding: "8px 16px" }}>
              Download as OpenDocument (.odt)
            </button>
          </div>
        </>
      )}
    </div>
  );
}
